// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeListItem } from '@/billing/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { formatShortDate } from '@/utilities/date-time';
import { formatNumber } from '@/utilities/number';
import { Box, Stack, Typography } from '@mui/material';

interface Props {
  charge: ChargeListItem;
}

/**
 * Read-only card summarising a single charge. Used by the cancel and revert-
 * cancellation dialogs so the user can confirm the right row is being acted
 * on. Mirrors the columns shown in the charges table.
 */
export function ChargePreviewCard(props: Props) {
  const { charge } = props;
  const servicePeriod =
    charge.servicePeriodStart && charge.servicePeriodEnd
      ? `${formatShortDate(charge.servicePeriodStart)} \u2013 ${formatShortDate(charge.servicePeriodEnd)}`
      : '\u2014';

  // Billable date mirrors the table cell: prefer actualBillableAt (the
  // moment the charge actually became billable); fall back to the scheduled
  // expectedBillableDate with an inline tag. Em dash when neither is set.
  let billableDate: string;
  if (charge.actualBillableAt) {
    billableDate = formatShortDate(charge.actualBillableAt);
  } else if (charge.expectedBillableDate) {
    billableDate = `${formatShortDate(charge.expectedBillableDate)} expected`;
  } else {
    billableDate = '\u2014';
  }

  const showUnitPrice =
    charge.pricingMode === 'PER_UNIT' && charge.unitPrice !== null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={0.75}>
        <ChargeDetailRow
          label="Customer"
          value={charge.project.customer?.name ?? '\u2014'}
        />
        <ChargeDetailRow label="Project" value={charge.project.name} />
        <ChargeDetailRow
          label="Item"
          value={`${charge.item.sku} (${charge.item.name})`}
        />
        <ChargeDetailRow label="Service period" value={servicePeriod} />
        <ChargeDetailRow label="Billable date" value={billableDate} />
        <ChargeDetailRow
          label="Quantity"
          value={formatNumber(charge.quantity)}
        />
        {showUnitPrice && charge.unitPrice !== null && (
          <ChargeDetailRow
            label="Unit price"
            value={formatCurrencyValue(charge.unitPrice)}
          />
        )}
        <ChargeDetailRow
          label="Amount"
          value={formatCurrencyValue(charge.totalPrice)}
        />
      </Stack>
    </Box>
  );
}

interface ChargeDetailRowProps {
  label: string;
  value: string;
  // Optional override for the label column's minimum width. Callers that mix
  // short and long labels in the same Stack should pass a value at least as
  // wide as the longest label so values stay visually aligned in a grid.
  // Defaults to 96.
  labelMinWidth?: number;
}

export function ChargeDetailRow(props: ChargeDetailRowProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="baseline">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: props.labelMinWidth ?? 96 }}
      >
        {props.label}
      </Typography>
      <Typography variant="body2">{props.value}</Typography>
    </Stack>
  );
}
